import { createContext, useContext } from 'react'
import type { FederatedProvider, Session, TokenResponse } from './types'

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

export interface AuthContextValue {
  status: AuthStatus
  /** True while the initial session restore or a sign-in is in flight. */
  isLoading: boolean
  isAuthenticated: boolean
  /** Bearer token for Hasura. Undefined unless authenticated. */
  accessToken: string | undefined
  /** Raw ID token, kept only for callers that need the original JWT. */
  idToken: string | undefined
  /** Identity and platform claims, from `GET /auth/session`. */
  session: Session | undefined
  /** Set when restoring or refreshing the session failed. */
  error: Error | undefined

  /** Adopt the tokens from a completed sign-in and load the session. */
  completeLogin: (tokens: TokenResponse) => Promise<void>
  /** Begin a federated sign-in by redirecting to the provider. */
  startFederatedLogin: (
    provider: FederatedProvider,
    options: { connectionId?: string | null; loginHint?: string; returnTo?: string }
  ) => Promise<void>
  /** Revoke the session and return to the sign-in page. */
  logout: () => Promise<void>
  /**
   * A valid access token, refreshing first if it is close to expiry. Returns
   * undefined once the session cannot be recovered.
   */
  getAccessToken: () => Promise<string | undefined>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth has to be used within <AuthProvider>')
  }
  return context
}
